import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { IntakeCard } from './IntakeCard';
import { useIntakes } from '@/hooks/useIntakeData';

export function IntakeList() {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  const { data, isLoading, error, refetch, isRefetching } = useIntakes({
    page: currentPage,
    pageSize,
  });

  // Debug logging
  console.log("🔍 IntakeList - data:", data);
  console.log("🔍 IntakeList - isLoading:", isLoading);
  console.log("🔍 IntakeList - error:", error);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-2 text-muted-foreground">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span>Loading intake forms...</span>
        </div>
      </div>
    );
  }

  if (error) {
    console.error("Intake forms error:", error);

    let errorMessage = "Failed to load intake forms";
    let errorDetails = "";

    if (error instanceof Error) {
      errorMessage = error.message;
    }

    // Handle specific API error responses
    if (error && typeof error === 'object' && 'status' in error) {
      const apiError = error as { status: number; message?: string };
      if (apiError.status === 401) {
        errorMessage = "Authentication required";
        errorDetails = "Please log in to view intake forms";
      } else if (apiError.status === 403) {
        errorMessage = "Access denied";
        errorDetails = "You don't have permission to view intake forms";
      } else if (apiError.status >= 500) {
        errorMessage = "Server error";
        errorDetails = "The intake forms service is temporarily unavailable";
      }
    }

    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center max-w-md">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <h3 className="text-lg font-semibold text-red-600 mb-2">{errorMessage}</h3>
          {errorDetails && (
            <p className="text-muted-foreground mb-4 text-sm">{errorDetails}</p>
          )}
          <div className="space-y-2">
            <Button onClick={() => refetch()} variant="outline" size="sm">
              Try Again
            </Button>
            {import.meta.env.DEV && (
              <details className="text-left">
                <summary className="cursor-pointer text-xs text-muted-foreground">Debug Info</summary>
                <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-auto">
                  {JSON.stringify(error, null, 2)}
                </pre>
              </details>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!data || data.records.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">No intake forms found</p>
        <Button onClick={() => refetch()} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>
    );
  }

  const { records, pagination } = data;

  return (
    <div className="space-y-6">
      {/* Header with refresh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-semibold">
            Intake Forms
            {data._stale && (
              <span className="ml-2 text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded">
                Cached Data
              </span>
            )}
          </h2>
          <Button
            onClick={() => refetch()}
            variant="outline"
            size="sm"
            disabled={isRefetching}
          >
            {isRefetching ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        </div>
        <div className="text-sm text-muted-foreground">
          {pagination.totalRecords} total forms
        </div>
      </div>

      {/* Grid of intake cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {records.map((intake) => (
          <IntakeCard key={intake.recordId} intake={intake} />
        ))}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Page {pagination.page} of {pagination.totalPages}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === pagination.totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}